/**
 * OAuthConsent — /oauth/consent (T-C2c, #1643 MCP-OAuth)
 *
 * Auth-gated Standalone-Consent-Seite des OAuth-2.1-Flows. Der Authorization-Endpoint
 * (/oauth/authorize) leitet hierher mit einem signierten `areq`-Handle. Diese Seite
 * zeigt Connector + angeforderte Scopes und lässt den angemeldeten WeShip-User die
 * Verbindung bestätigen (→ Auth-Code) oder ablehnen. Danach Redirect zurück zu Claude.ai.
 */
import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Plug, ShieldCheck, Loader2 } from 'lucide-react';

export default function OAuthConsent() {
  const { t } = useLanguage();
  const areq = new URLSearchParams(window.location.search).get('areq') || '';

  const [info, setInfo] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | error | submitting
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!areq) { setState('error'); setErrMsg(t('oauthConsent.errInvalid')); return; }
      try {
        const r = await fetch(`/api/oauth/authorize/info?areq=${encodeURIComponent(areq)}`, { credentials: 'include' });
        if (!alive) return;
        if (!r.ok) { setState('error'); setErrMsg(t('oauthConsent.errInvalid')); return; }
        setInfo(await r.json());
        setState('ready');
      } catch {
        if (alive) { setState('error'); setErrMsg(t('oauthConsent.errInvalid')); }
      }
    })();
    return () => { alive = false; };
  }, [areq]);

  async function decide(approve) {
    setState('submitting');
    try {
      const r = await fetch('/api/oauth/authorize/decision', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ areq, approve }),
      });
      const d = await r.json();
      if (r.ok && d.redirect) { window.location.href = d.redirect; return; }
      setState('error'); setErrMsg(t('oauthConsent.errInvalid'));
    } catch {
      setState('error'); setErrMsg(t('oauthConsent.errInvalid'));
    }
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[var(--bg-inset,#F8F9FC)] p-4">
      <div className="w-full max-w-md rounded-2xl bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-[var(--primary,#338edc)]/10 flex items-center justify-center">
            <Plug size={20} className="text-[var(--primary,#338edc)]" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[var(--text-primary)]">{t('oauthConsent.title')}</h1>
            <p className="text-xs text-[var(--text-tertiary)]">{t('oauthConsent.subtitle')}</p>
          </div>
        </div>

        {state === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-10 text-[var(--text-tertiary)] text-sm">
            <Loader2 size={16} className="animate-spin" /> {t('common.loading')}…
          </div>
        )}

        {state === 'error' && (
          <div className="py-6 text-center text-sm text-red-600">{errMsg}</div>
        )}

        {(state === 'ready' || state === 'submitting') && info && (
          <>
            <p className="text-sm text-[var(--text-secondary)] mb-4 leading-relaxed">
              {t('oauthConsent.intro', { client: info.client_name })}
            </p>

            <div className="rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] p-4 mb-4 space-y-3">
              <Row label={t('oauthConsent.connector')} value={info.client_name} />
              <Row label={t('oauthConsent.account')} value={`${info.user?.name || ''} · ${info.user?.email || ''}`} />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1.5">{t('oauthConsent.permissions')}</div>
                <div className="space-y-1.5">
                  {(info.scopes || []).map(s => (
                    <div key={s} className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
                      <ShieldCheck size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{t(`oauthConsent.scope.${s}`, s)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <p className="text-[11px] text-[var(--text-tertiary)] mb-4 leading-relaxed">
              {t('oauthConsent.redirectNote', { host: info.redirect_host })}
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => decide(false)} disabled={state === 'submitting'}
                className="flex-1 px-4 py-2.5 rounded-lg border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
              >
                {t('oauthConsent.deny')}
              </button>
              <button
                onClick={() => decide(true)} disabled={state === 'submitting'}
                className="flex-1 px-4 py-2.5 rounded-lg bg-[var(--primary,#338edc)] text-white text-sm font-medium hover:bg-[var(--primary,#338edc)]/90 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {state === 'submitting' && <Loader2 size={14} className="animate-spin" />}
                {t('oauthConsent.approve')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-0.5">{label}</div>
      <div className="text-sm text-[var(--text-primary)] font-medium break-words">{value}</div>
    </div>
  );
}
